// Pure helper — beslist of het auto-lock-moment van een ISO-week verstreken is.
//
// Géén I/O, géén side-effects. DST-veilig via de bestaande
// `amsterdamWallclockToUtc`-helper (twee-pass offset-solve, zie amsterdam-tijd.ts).
//
// Tijdzone-conventie (CLAUDE.md §Tijdzone, locale & geld):
//   • "Einde van de dag in Europe/Amsterdam" = 23:59:59 NL = een DST-afhankelijk
//     UTC-instant: 22:59:59Z (winter, CET = UTC+1) of 21:59:59Z (zomer, CEST = UTC+2).
//   • Nooit hardcoded UTC-offsets — altijd via `amsterdamWallclockToUtc`.

import { getWeekDateRange } from './uren-calculations'
import { amsterdamWallclockToUtc } from './amsterdam-tijd'

// ─── Types ────────────────────────────────────────────────────────────────────

export type WeekAfsluitMoment =
  | 'ZATERDAGNACHT'
  | 'ZONDAGNACHT'
  | 'MAANDAGAVOND'
  | 'DINSDAGAVOND'
  | 'WOENSDAGAVOND'
  | 'HANDMATIG_VOORSTEL'

/**
 * Aantal dagen NA de zondag-die-de-week-afsluit waarop de lock valt.
 *
 * ZONDAGNACHT = 0: de zondagnacht van de week zelf.
 * MAANDAGAVOND = 1 t/m WOENSDAGAVOND = 3: volgende week.
 * ZATERDAGNACHT = 6: de zaterdag van de volgende week.
 * HANDMATIG_VOORSTEL is uitsluitend handmatig — nooit automatisch.
 */
export const LOCK_GRACE_DAGEN: Record<Exclude<WeekAfsluitMoment, 'HANDMATIG_VOORSTEL'>, number> = {
  ZONDAGNACHT: 0,
  MAANDAGAVOND: 1,
  DINSDAGAVOND: 2,
  WOENSDAGAVOND: 3,
  ZATERDAGNACHT: 6,
}

// ─── Interne helper ───────────────────────────────────────────────────────────

/** Formatteer een UTC-midnight Date als 'yyyy-mm-dd' (voor amsterdamWallclockToUtc). */
function toYmd(d: Date): string {
  const y = d.getUTCFullYear()
  const m = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * UTC-instant van 23:59:59 Amsterdam-wandkloktijd op de gegeven UTC-midnight Date,
 * verschoven met `graceDagen` kalenderdagen.
 *
 * Methode:
 * 1. Neem de UTC-midnight van de afsluit-zondag (= `.end` van getWeekDateRange).
 * 2. Tel `graceDagen` UTC-dagen op (veilig: dag-overgang in UTC, geen DST-probleem).
 * 3. Bereken 00:00:00 Amsterdam op de VOLGENDE kalenderdag (graceDagen + 1) via
 *    `amsterdamWallclockToUtc(nextDayYmd, '00:00')`.
 * 4. Trek 1 seconde af → 23:59:59 Amsterdam-wandkloktijd op de lock-dag.
 *
 * Dit vermijdt het gebruik van '23:59:59' als tijdstring (parseHm accepteert alleen HH:mm)
 * én is DST-veilig: het "einde van de dag" is één seconde vóór het begin van de
 * volgende dag in NL-wandkloktijd.
 */
function lockMoment(sundayUtcMidnight: Date, graceDagen: number): Date {
  // De dag NADAT de lock-dag eindigt, in UTC-milliseconden.
  const nextDayMs = sundayUtcMidnight.getTime() + (graceDagen + 1) * 24 * 60 * 60 * 1000
  const nextDayUtcMidnight = new Date(nextDayMs)
  const nextDayYmd = toYmd(nextDayUtcMidnight)
  // 00:00:00 NL op de volgende dag, minus 1 seconde = 23:59:59 NL op de lock-dag.
  const nextDayStart = amsterdamWallclockToUtc(nextDayYmd, '00:00')
  return new Date(nextDayStart.getTime() - 1000)
}

// ─── Publieke API ─────────────────────────────────────────────────────────────

/**
 * True als het auto-lock-moment van ISO-week `(year, week)` verstreken is op `now`.
 *
 * Het lock-moment is het einde van de kalenderdag (23:59:59 Amsterdam-wandkloktijd)
 * die `LOCK_GRACE_DAGEN[afsluitmoment]` dagen na de afsluit-zondag van die week valt.
 *
 * HANDMATIG_VOORSTEL geeft altijd false — die weken worden nooit automatisch gesloten.
 *
 * @param afsluitmoment  Geconfigureerd afsluitmoment van de BV.
 * @param year           ISO-jaar van de week.
 * @param week           ISO-weeknummer.
 * @param now            Huidig UTC-instant (typisch `new Date()`).
 * @returns              True als het lock-moment verstreken is (now >= lock-moment).
 */
export function weekstaatLockDue(
  afsluitmoment: WeekAfsluitMoment,
  year: number,
  week: number,
  now: Date,
): boolean {
  if (afsluitmoment === 'HANDMATIG_VOORSTEL') return false

  const { end: sundayUtcMidnight } = getWeekDateRange(year, week)
  const graceDagen = LOCK_GRACE_DAGEN[afsluitmoment]
  const lock = lockMoment(sundayUtcMidnight, graceDagen)

  return now.getTime() >= lock.getTime()
}
